import { useTranslation } from 'react-i18next';
import { Controller, UseFormReturn } from 'react-hook-form';
import FormField from '../../core/components/FormField.tsx';
import StringField from '../../core/components/Fields/StringField.tsx';
import SelectField from '../../core/components/Fields/SelectField.tsx';
import BooleanField from '../../core/components/Fields/BooleanField.tsx';
import Alert from '../../core/components/Alert.tsx';
import { DELIVERY_METHODS, useUploadConfigFieldState } from '../uploadConfigFields.ts';

interface UploadConfigFormFieldsProps {
    form: UseFormReturn<any>;
}

const UploadConfigFormFields = ({ form }: UploadConfigFormFieldsProps) => {
    const { t } = useTranslation('config');
    const formErrors = form.formState.errors;
    const { enabled, isApplicable } = useUploadConfigFieldState(form);

    const deliveryMethodOptions = DELIVERY_METHODS.map((method) => ({
        value: method,
        label: t(`configPage.upload.deliveryMethodNames.${method}`),
        description: t(`configPage.upload.deliveryMethodDescriptions.${method}`),
    }));

    return (
        <>
            <Controller
                name="deliveryMethod"
                control={form.control}
                render={({ field }) => (
                    <FormField
                        label={t('configPage.upload.form.deliveryMethod')}
                        error={formErrors.deliveryMethod}
                        required
                    >
                        <SelectField
                            {...field}
                            options={deliveryMethodOptions}
                            placeholder={t('configPage.upload.form.deliveryMethodPlaceholder')}
                            required
                        />
                    </FormField>
                )}
            />

            {isApplicable('enabled') && (
                <Controller
                    name="enabled"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.upload.form.enabled')}
                            helpText={t('configPage.upload.form.enabledDescription')}
                            error={formErrors.enabled}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />
            )}

            {isApplicable('enabled') && !enabled && (
                <Alert severity="warning">{t('configPage.upload.form.pushPausedNotice')}</Alert>
            )}

            {isApplicable('targetPathFileExport') && (
                <Controller
                    name="targetPathFileExport"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.upload.form.targetPathFileExport')}
                            error={formErrors.targetPathFileExport}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />
            )}

            {isApplicable('targetPathOriginalDocument') && (
                <Controller
                    name="targetPathOriginalDocument"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.upload.form.targetPathOriginalDocument')}
                            error={formErrors.targetPathOriginalDocument}
                        >
                            <StringField {...field} />
                        </FormField>
                    )}
                />
            )}

            {isApplicable('sendOriginalDocument') && (
                <Controller
                    name="sendOriginalDocument"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.upload.form.sendOriginalDocument')}
                            error={formErrors.sendOriginalDocument}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />
            )}

            {isApplicable('sendOriginalEmail') && (
                <Controller
                    name="sendOriginalEmail"
                    control={form.control}
                    render={({ field }) => (
                        <FormField
                            label={t('configPage.upload.form.sendOriginalEmail')}
                            error={formErrors.sendOriginalEmail}
                        >
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />
            )}
        </>
    );
};

export default UploadConfigFormFields;
